import React, { useCallback, useRef, useState } from 'react';

import type { InteractionSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import type { StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import type { RemixOptionKind } from '../remix/remix-overlay/types';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	ContentClassification,
	DynamicUiSource,
	EditorRemixLifecycleAdapter,
	RemixContentComponentFullProps,
	RemixFinalizeFailedPayload,
	RemixFinalizeSuccessPayload,
	RemixHistoryEntryForUpdate,
	RemixInsertSuccessPayload,
	RegisterRemixResultTracking,
	WhiteboardEmbedReplacementCallback,
} from './types';

type EditorRemixContextType = {
	editorRemixLifecycleAdapter?: EditorRemixLifecycleAdapter;
	/** Whether the editor migration treatment owns this Remix surface. */
	isEditorRemixStateRearchitectureTreatment?: boolean;
	entryPoint?: string;
	smartCreationConfig?: RemixContentComponentFullProps['smartCreationConfig'];
	smartCreationContext?: RemixContentComponentFullProps['smartCreationContext'];
	productConfig?: RemixContentComponentFullProps['productConfig'];

	isViewMode?: boolean;
	isRemixPreviewVisible?: boolean;

	/** The type of remix that is currently selected. */
	dynamicUiType?: RemixOptionKind;
	setDynamicUiType: (type: RemixOptionKind | undefined) => void;

	/** The subtype of the remix that is currently selected. */
	dynamicUiSubtype?: string;
	setDynamicUiSubtype: (subtype: string | undefined) => void;

	dynamicUiSource?: DynamicUiSource;
	setDynamicUiSource: (source: DynamicUiSource | undefined) => void;

	/** The conversation ID of the remix conversation. */
	conversationId?: string;
	setConversationId: (conversationId: string | undefined) => void;

	/** Identifies the current streaming attempt, including retries on the same conversation. */
	streamingAttemptId?: string;
	setStreamingAttemptId: (streamingAttemptId: string | undefined) => void;

	/** The interaction source that triggered this remix (e.g. 'remixModalFromMediaInserts'). */
	interactionSource?: InteractionSource;
	setInteractionSource: (interactionSource: InteractionSource | undefined) => void;

	/**
	 * The aspect ratio selected for AI image generation (e.g. "16:9", "1:1").
	 * Set when the remix originates from the `ImageFromAIGeneration` component
	 * (via `remixModalMetadata.aspectRatio`). Propagated end-to-end to the editor
	 * insert-content bridge command so the inserted image can be sized to its
	 * intended container.
	 */
	aspectRatio?: string | null;
	setAspectRatio: (aspectRatio: string | null | undefined) => void;

	/** The final complete message that is returned from the remix. */
	finalMessage?: StoreAssistant;
	setFinalMessage: (message: StoreAssistant | undefined) => void;

	/**
	 * Normalized classification for the content currently being streamed into Remix.
	 * Classification is sourced from ACTION_CLASSIFICATION, while `data` is filled by
	 * the first content-specific ACTION_PART payload so insertions wait for stream output.
	 */
	contentClassification?: ContentClassification;
	setContentClassification: React.Dispatch<React.SetStateAction<ContentClassification | undefined>>;

	/** A callback that clears the editor-owned thinking decoration without completing the flow. */
	clearThinkingDecoration?: () => void;
	/** Runs synchronously before Remix itself replaces the tracked source or update target. */
	onBeforeEditorRemixResultInsertion?: () => void;

	/** Replaces an early streaming whiteboard embed with its finalized canonical URL. */
	replaceWhiteboardEmbed?: WhiteboardEmbedReplacementCallback;

	/** Drives the MAUI "visible to others" banner for a whiteboard embed; set by the Rovo remix finalizer. */
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;

	/** A callback that is called when the thinking is complete. */
	setThinkingComplete?: (isComplete: boolean) => void;
	setThinkingWaitingForInput?: (isWaitingForInput: boolean) => void;

	/** A callback that finalizes the active remix thinking lifecycle. */
	finishRemixThinking?: () => void;

	/** Whether the location picker is currently shown. */
	showLocationPicker: boolean;
	setShowLocationPicker: (show: boolean) => void;

	/**
	 * Adds an entry to the remix history. This is used to keep track of the different remixes
	 * that have been done in the editor and their associated conversation and message data.
	 * This allows the user to navigate back and forth between different remixes and see the associated conversation history for each remix.
	 */
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;

	/**
	 * Adds an entry to the remix history stack.
	 */
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;

	/**
	 * Fired by `useOnComplete` once a variant-picker update settles. Hosts wire
	 * it to `trackVariantUpdateComplete`; `id` matches the value the toolbar
	 * registered via `trackVariantUpdateStart` (image fieldId or MAUI app id).
	 */
	onVariantUpdateComplete?: (id: string) => void;

	/**
	 * Fired when the backend produced a different subtype than selected. Hosts
	 * wire it to show the "Output adjusted" flag.
	 */
	onSubtypeMismatch?: () => void;

	/**
	 * Fired after a newly-created remix has been inserted into the editor.
	 * Product integrations can use this to run post-insert side effects.
	 */
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;

	/** Fired after automatic finalization of a remixed whiteboard succeeds. */
	onFinalizeSuccess?: (payload: RemixFinalizeSuccessPayload) => void;

	/** Records a Remix result for retention observation and optional delayed acceptance analytics. */
	registerRemixResultTracking?: RegisterRemixResultTracking;

	/**
	 * Fired when automatic finalization of a remixed whiteboard fails. Product
	 * integrations may take over the recovery UX and return `true` to signal the
	 * failure was handled.
	 */
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;

	/** Whether the remix is an update. */
	isUpdate?: boolean;
	/** The history entry for the update. */
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	/**
	 * Id of the final message displayed on the page when an update was initiated.
	 * Captured live in useUpdateRemix and read by the useRemixToAIFC dedup so the
	 * previously-displayed result is suppressed until the new variant response
	 * arrives. A ref (not state) so it is readable synchronously without a re-render,
	 * and because stored remix-history ids can be stale for BE-driven edits.
	 */
	remixUpdateSentinelRef: React.MutableRefObject<string | undefined>;
	/**
	 * Whether insertion into the document has begun for the current flow.
	 *
	 * Set by `useOnComplete` at the single point insertion starts, and read by the lifecycle-phase
	 * projection reported for migration shadow parity. A ref rather than state because this is only
	 * observed by diagnostics: promoting it to state would re-render the Remix subtree during
	 * insertion purely to feed an observer.
	 */
	isRemixInsertingRef: React.MutableRefObject<boolean>;
	/** Retained whiteboard flow that still owns insertion cancellation, before render handoff. */
	pendingRemixInsertFlowIdRef: React.MutableRefObject<string | undefined>;
	/** The dynamic UI subtype for the update. */
	dynamicUiSubtypeForUpdate?: string;

	/** A callback that is called when the remix thinking complete animation is finished. */
	onRemixThinkingCompleteAnimationFinished?: () => void;

	/** Resets the context to the initial state. */
	resetContext: () => void;
};

export const EditorRemixContext: React.Context<EditorRemixContextType> =
	React.createContext<EditorRemixContextType>({
		editorRemixLifecycleAdapter: undefined,
		entryPoint: undefined,
		smartCreationConfig: undefined,
		smartCreationContext: undefined,
		productConfig: undefined,
		isViewMode: undefined,
		dynamicUiType: undefined,
		setDynamicUiType: (type: RemixOptionKind | undefined) => {},
		dynamicUiSubtype: undefined,
		setDynamicUiSubtype: (subtype: string | undefined) => {},
		dynamicUiSource: undefined,
		setDynamicUiSource: (source: DynamicUiSource | undefined) => {},
		conversationId: undefined,
		setConversationId: (conversationId: string | undefined) => {},
		streamingAttemptId: undefined,
		setStreamingAttemptId: (streamingAttemptId: string | undefined) => {},
		interactionSource: undefined,
		setInteractionSource: (interactionSource: InteractionSource | undefined) => {},
		aspectRatio: undefined,
		setAspectRatio: (aspectRatio: string | null | undefined) => {},
		finalMessage: undefined,
		setFinalMessage: (message: StoreAssistant | undefined) => {},
		contentClassification: undefined,
		setContentClassification: (
			classification: React.SetStateAction<ContentClassification | undefined>,
		) => {},
		clearThinkingDecoration: () => {},
		replaceWhiteboardEmbed: () => false,
		setMauiEmbedFinalizeStatus: () => {},
		setThinkingComplete: (isComplete: boolean) => {},
		setThinkingWaitingForInput: (isWaitingForInput: boolean) => {},
		finishRemixThinking: () => {},
		showLocationPicker: false,
		setShowLocationPicker: (show: boolean) => {},
		addHistoryEntry: (options: AddRemixHistoryEntryPayload) => {},
		addHistoryStackEntry: (options: AddRemixHistoryStackEntryPayload) => {},
		onVariantUpdateComplete: (id: string) => {},
		onRemixInsertSuccess: (payload: RemixInsertSuccessPayload) => {},
		onFinalizeSuccess: undefined,
		onFinalizeFailed: undefined,
		registerRemixResultTracking: undefined,
		isUpdate: undefined,
		historyEntryForUpdate: undefined,
		remixUpdateSentinelRef: { current: undefined },
		isRemixInsertingRef: { current: false },
		pendingRemixInsertFlowIdRef: { current: undefined },
		dynamicUiSubtypeForUpdate: undefined,
		onRemixThinkingCompleteAnimationFinished: () => {},
		resetContext: () => {},
	});

export const useEditorRemixContext = (): EditorRemixContextType => {
	return React.useContext(EditorRemixContext);
};

export const EditorRemixContextProvider = ({
	editorRemixLifecycleAdapter,
	isEditorRemixStateRearchitectureTreatment,
	entryPoint,
	isViewMode,
	isRemixPreviewVisible = false,
	clearThinkingDecoration,
	onBeforeEditorRemixResultInsertion,
	replaceWhiteboardEmbed,
	setMauiEmbedFinalizeStatus,
	setThinkingComplete,
	setThinkingWaitingForInput,
	finishRemixThinking,
	showLocationPicker = false,
	setShowLocationPicker = () => {},
	smartCreationConfig,
	smartCreationContext,
	productConfig,
	addHistoryEntry,
	addHistoryStackEntry,
	onVariantUpdateComplete,
	onSubtypeMismatch,
	onRemixInsertSuccess,
	onFinalizeSuccess,
	onFinalizeFailed,
	registerRemixResultTracking,
	isUpdate,
	historyEntryForUpdate,
	dynamicUiSubtypeForUpdate,
	onRemixThinkingCompleteAnimationFinished,
	children,
}: {
	editorRemixLifecycleAdapter?: EditorRemixLifecycleAdapter;
	isEditorRemixStateRearchitectureTreatment?: boolean;
	entryPoint?: string;
	isViewMode?: boolean;
	isRemixPreviewVisible?: boolean;
	clearThinkingDecoration?: () => void;
	onBeforeEditorRemixResultInsertion?: () => void;
	replaceWhiteboardEmbed?: WhiteboardEmbedReplacementCallback;
	setMauiEmbedFinalizeStatus?: (options: {
		contentId: string;
		status: 'suppressed' | 'needs-finalize';
	}) => void;
	setThinkingComplete?: (isComplete: boolean) => void;
	setThinkingWaitingForInput?: (isWaitingForInput: boolean) => void;
	finishRemixThinking?: () => void;
	showLocationPicker?: boolean;
	setShowLocationPicker?: (showLocationPicker: boolean) => void;
	addHistoryEntry?: (options: AddRemixHistoryEntryPayload) => void;
	addHistoryStackEntry?: (options: AddRemixHistoryStackEntryPayload) => void;
	onVariantUpdateComplete?: (id: string) => void;
	onSubtypeMismatch?: () => void;
	isUpdate?: boolean;
	historyEntryForUpdate?: RemixHistoryEntryForUpdate;
	dynamicUiSubtypeForUpdate?: string;
	smartCreationConfig?: RemixContentComponentFullProps['smartCreationConfig'];
	smartCreationContext?: RemixContentComponentFullProps['smartCreationContext'];
	productConfig?: RemixContentComponentFullProps['productConfig'];
	onRemixInsertSuccess?: (payload: RemixInsertSuccessPayload) => void;
	onFinalizeSuccess?: (payload: RemixFinalizeSuccessPayload) => void;
	onFinalizeFailed?: (payload: RemixFinalizeFailedPayload) => boolean;
	registerRemixResultTracking?: RegisterRemixResultTracking;
	onRemixThinkingCompleteAnimationFinished?: () => void;
	children: React.ReactNode;
}): React.JSX.Element => {
	const [dynamicUiType, setDynamicUiType] = useState<RemixOptionKind | undefined>(undefined);
	const [dynamicUiSubtype, setDynamicUiSubtype] = useState<string | undefined>(undefined);
	const [dynamicUiSource, setDynamicUiSource] = useState<DynamicUiSource | undefined>(undefined);
	const [conversationId, setConversationId] = useState<string | undefined>(undefined);
	const [streamingAttemptId, setStreamingAttemptId] = useState<string | undefined>(undefined);
	const [interactionSource, setInteractionSource] = useState<InteractionSource | undefined>(
		undefined,
	);
	const [aspectRatio, setAspectRatio] = useState<string | null | undefined>(undefined);
	const [finalMessage, setFinalMessage] = useState<StoreAssistant | undefined>(undefined);
	const [contentClassification, setContentClassification] = useState<
		ContentClassification | undefined
	>(undefined);
	const remixUpdateSentinelRef = useRef<string | undefined>(undefined);
	const isRemixInsertingRef = useRef(false);
	const pendingRemixInsertFlowIdRef = useRef<string | undefined>(undefined);

	const resetContext = useCallback(() => {
		setDynamicUiType(undefined);
		setDynamicUiSubtype(undefined);
		setDynamicUiSource(undefined);
		setConversationId(undefined);
		setStreamingAttemptId(undefined);
		setInteractionSource(undefined);
		setAspectRatio(undefined);
		setFinalMessage(undefined);
		setContentClassification(undefined);
		remixUpdateSentinelRef.current = undefined;
		isRemixInsertingRef.current = false;
	}, []);

	return (
		<EditorRemixContext.Provider
			value={{
				editorRemixLifecycleAdapter,
				isEditorRemixStateRearchitectureTreatment,
				entryPoint,
				smartCreationConfig,
				smartCreationContext,
				productConfig,
				isViewMode,
				isRemixPreviewVisible,
				dynamicUiType,
				setDynamicUiType,
				dynamicUiSubtype,
				setDynamicUiSubtype,
				dynamicUiSource,
				setDynamicUiSource,
				conversationId,
				setConversationId,
				streamingAttemptId,
				setStreamingAttemptId,
				interactionSource,
				setInteractionSource,
				aspectRatio,
				setAspectRatio,
				finalMessage,
				setFinalMessage,
				contentClassification,
				setContentClassification,
				clearThinkingDecoration,
				onBeforeEditorRemixResultInsertion,
				replaceWhiteboardEmbed,
				setMauiEmbedFinalizeStatus,
				setThinkingComplete,
				setThinkingWaitingForInput,
				finishRemixThinking,
				showLocationPicker,
				setShowLocationPicker,
				addHistoryEntry,
				addHistoryStackEntry,
				onVariantUpdateComplete,
				onSubtypeMismatch,
				onRemixInsertSuccess,
				onFinalizeSuccess,
				onFinalizeFailed,
				registerRemixResultTracking,
				isUpdate,
				historyEntryForUpdate,
				remixUpdateSentinelRef,
				isRemixInsertingRef,
				pendingRemixInsertFlowIdRef,
				dynamicUiSubtypeForUpdate,
				onRemixThinkingCompleteAnimationFinished,
				resetContext,
			}}
		>
			{children}
		</EditorRemixContext.Provider>
	);
};
